import React, {
  ReactElement,
  useState,
  Ref,
  forwardRef,
  useImperativeHandle,
  useId,
  useRef,
} from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import AurIcon, {IconNames} from '../aur-icon/AurIcon';
import AurButton from '../aur-button/AurButton';
import {Box, SvgIconProps} from '@mui/material';
import AurSvgIcon from '../aur-svg-icon/AurSvgIcon';
import {t} from 'i18next';
import { scrollToAccordion } from '../../../utils/utils';

type AurAccordionProps = {
  disabled?: boolean;
  collapsed?: boolean;
  fullWidth?: boolean;
  transparent?: boolean;
  title: string | ReactElement;
  titleContent?: string | ReactElement | ReactElement[];
  children?: string | ReactElement | ReactElement[];
  className?: string;
  onExpand?: () => void;
  onCollapse?: () => void;
  onToggle?: () => void;
  toggleButtonTestId?: string;
  alertIcon?: React.ReactElement<SvgIconProps>;
  alertWarningIcon: boolean;
  alertCount?: string;
  accordionAriaLabel?: string;
  dataTestId?: string;
  toggleButtonExpandHint?: string;
  toggleButtonCollapseHint?: string;
  showPointerCursorWithinTitle?: boolean;
  hideToggleButton?: boolean;
  enableAutoScrollOnExpand?: boolean;
};

export interface AurAccordionRef {
  expand: () => void;
  collapse: () => void;
}

const AurAccordion = forwardRef(
  (props: AurAccordionProps, ref: Ref<AurAccordionRef>) => {
    const {showPointerCursorWithinTitle = false, hideToggleButton = false, enableAutoScrollOnExpand = false,} =
      props;
    const [expanded, setExpanded] = useState(!props.collapsed);
    const hintID = useId();
    const cardRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => ({expand, collapse}));

    const handleOnChange = () => {
      const isExpanded = !expanded;
      setExpanded(isExpanded);

      if (isExpanded) {
        props.onExpand?.();
      } else {
        props.onCollapse?.();
      }

      props.onToggle?.();
    };

    const expand = () => {
      setExpanded(true);
    };

    const collapse = () => {
      setExpanded(false);
    };

    const toggleIcon = () => {
      if (expanded) {
        return <AurIcon icon={IconNames.ChevronUp} />;
      }
      return <AurIcon icon={IconNames.ChevronDown} />;
    };

    const ariaLabel = () => {
      if (expanded) {
        return props.toggleButtonCollapseHint ?? t('common.showLessLabel');
      }
      return props.toggleButtonExpandHint ?? t('common.showMoreLabel');
    };

    const classNames = [];
    if (props.className) {
      classNames.push(props.className);
    }
    if (props.fullWidth) {
      classNames.push('full-width');
    }
    if (props.disabled) {
      classNames.push('disabled');
    }
    if (props.transparent) {
      classNames.push('transparent');
    }
    if (expanded) {
      classNames.push('expanded');
    } else {
      classNames.push('collapsed');
    }
    const className = classNames.join(' ');
    return (
      <Card
        ref={cardRef}
        aria-disabled={props.disabled}
        className={`aur-accordion ${className}`}
        aria-label={props.accordionAriaLabel}
        data-testid={props.dataTestId}
      >
        <fieldset disabled={props.disabled}>
          <CardContent
            data-testid="accordion-header"
            className={`aur-accordion-header ${showPointerCursorWithinTitle ? 'aur-accordion-header-default-cursor' : ''}`}
          >
            <div
              className={`aur-accordion-toggle ${hideToggleButton ? 'hide-aur-accordion-toggle' : ''}`}
            >
              <span id={hintID} style={{display: 'none'}}>
                {ariaLabel()}
              </span>
              <AurButton
                dataTestId={props.toggleButtonTestId || 'toggle-btn'}
                type="text"
                ariaLabel={ariaLabel()}
                onClick={handleOnChange}
                startIcon={toggleIcon()}
                ariaDescribedBy={hintID}
              />
            </div>
            {props.alertWarningIcon ? (
              <div className="welcome-alert-icon-container">
                <div className="alert-svg-container">
                  <AurSvgIcon src="../assests/images/report-warning.svg" />
                </div>

                <div className="alert-count">{props.alertCount}</div>
              </div>
            ) : null}
            <div
              className="aur-accordion-title"
              role="button"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleOnChange();
                }
              }}
              onClick={handleOnChange}
              tabIndex={0}
              data-testid="accordion-title-div"
            >
              {typeof props.title == 'string' ? (
                <Typography className="typo-text headline2_size">
                  {props.title}
                </Typography>
              ) : (
                <Box
                  data-testid="custom-title-container"
                  className={
                    showPointerCursorWithinTitle
                      ? 'custom-title-content-cursor'
                      : ''
                  }
                >
                  {props.title}
                </Box>
              )}
            </div>
            <div className="aur-accordion-title-content">
              {props.titleContent}
            </div>
          </CardContent>
          <Collapse
            in={expanded}
            timeout="auto"
            unmountOnExit
            className="aur-accordion-body"
            data-testid="collapse-element"
            id="accordion-content"
            onEntered={() => {
              if (enableAutoScrollOnExpand) {
                scrollToAccordion(cardRef.current);
              }
            }}
          >
            <CardContent>{props.children}</CardContent>
          </Collapse>
        </fieldset>
      </Card>
    );
  }
);

export default AurAccordion;
